import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, Copy, CreditCard, Landmark, Smartphone } from "lucide-react";
import { api, eur, formatErr } from "@/lib/api";

export default function PaymentInfo() {
  const { orderId } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}/payment-info`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(formatErr(e.response?.data?.detail) || e.message));
  }, [orderId]);

  const copy = async (val, key) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  if (err) return <main className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-info-error"><p style={{ color: "#9E4747" }}>{err}</p><Link to="/loja" className="btn-primary mt-6 inline-block">Voltar à loja</Link></main>;
  if (!info) return <main className="max-w-2xl mx-auto px-6 py-32 text-center brand-muted">A carregar...</main>;

  const method = info.payment_method;
  const details = info.method_details || {};

  return (
    <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16" data-testid="page-payment-info">
      <div className="text-center mb-10">
        {info.payment_status === "paid" ? (
          <CheckCircle2 size={48} className="mx-auto text-[#4A6B53] mb-6" />
        ) : (
          <Clock size={48} className="mx-auto brand-primary-text mb-6" />
        )}
        <p className="label-sm mb-3">Encomenda confirmada</p>
        <h1 className="font-serif text-4xl lg:text-5xl brand-text mb-4">Informações de pagamento</h1>
      </div>

      <div className="brand-surface p-8 lg:p-10">
        <p className="brand-text whitespace-pre-line leading-relaxed mb-8" data-testid="payment-message">{info.message}</p>

        <div className="grid grid-cols-2 gap-6 pb-6 border-b brand-border mb-6">
          <div>
            <p className="label-sm mb-2">Número da encomenda</p>
            <p className="font-serif text-2xl brand-text" data-testid="order-number">#{info.order_number}</p>
          </div>
          <div className="text-right">
            <p className="label-sm mb-2">Valor a pagar</p>
            <p className="font-serif text-2xl brand-primary-text" data-testid="order-total">{eur(info.amount)}</p>
          </div>
        </div>

        <div className="mb-6">
          <p className="label-sm mb-3">Método escolhido</p>
          {method === "mbway" && (
            <div className="flex items-center gap-3" data-testid="method-mbway">
              <Smartphone size={20} className="brand-primary-text" />
              <span className="font-medium brand-text">MB WAY</span>
            </div>
          )}
          {method === "transfer" && (
            <div className="flex items-center gap-3" data-testid="method-transfer">
              <Landmark size={20} className="brand-primary-text" />
              <span className="font-medium brand-text">Transferência bancária</span>
            </div>
          )}
        </div>

        {method === "mbway" && details.number && (
          <div className="space-y-3" data-testid="mbway-details">
            <div className="brand-bg p-4 border brand-border flex items-center justify-between">
              <div>
                <p className="label-sm mb-1">Número MB WAY</p>
                <p className="font-mono text-xl brand-text" data-testid="mbway-number">{details.number}</p>
              </div>
              <button onClick={() => copy(details.number, "mb")} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="copy-mbway">
                <Copy size={12} /> {copied === "mb" ? "Copiado!" : "Copiar"}
              </button>
            </div>
            {details.instructions && <p className="text-sm brand-muted">{details.instructions}</p>}
          </div>
        )}

        {method === "transfer" && (
          <div className="space-y-3" data-testid="transfer-details">
            {details.holder && (
              <div className="brand-bg p-4 border brand-border">
                <p className="label-sm mb-1">Titular</p>
                <p className="brand-text">{details.holder}</p>
              </div>
            )}
            {details.iban && (
              <div className="brand-bg p-4 border brand-border flex items-center justify-between">
                <div>
                  <p className="label-sm mb-1">IBAN</p>
                  <p className="font-mono brand-text" data-testid="iban-value">{details.iban}</p>
                </div>
                <button onClick={() => copy(details.iban, "iban")} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="copy-iban">
                  <Copy size={12} /> {copied === "iban" ? "Copiado!" : "Copiar"}
                </button>
              </div>
            )}
            {details.bic && (
              <div className="brand-bg p-4 border brand-border">
                <p className="label-sm mb-1">BIC / SWIFT</p>
                <p className="font-mono brand-text">{details.bic}</p>
              </div>
            )}
            {details.instructions && <p className="text-sm brand-muted mt-3">{details.instructions}</p>}
          </div>
        )}

        {(!method || (!details.number && !details.iban)) && (
          <p className="text-sm" style={{ color: "#9E4747" }} data-testid="method-missing">
            Dados de pagamento ainda não configurados. Contacte-nos por email para receber as instruções.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Link to="/conta" className="btn-primary" data-testid="link-my-orders">Ver as minhas encomendas</Link>
        <Link to="/loja" className="btn-ghost" data-testid="link-continue-shopping">Continuar a comprar</Link>
      </div>
    </main>
  );
}
